import { useCallback, useContext, useRef } from "react";
import { useSelector } from "react-redux";
import { Feature, LineString } from "geojson";
import { delay } from "lodash";

import { openStreetViewUrl } from "fond/utils/mapbox";

import { beginMeasuring, endMeasuring } from "../project/redux";
import { Store, Version } from "../types";
import { useAppDispatch } from "../utils/hooks";

import BaseMapButtons from "./BaseMapButtons";
import { MapContext } from "./MapProvider";
import { viewDesign, zoomIn, zoomOut } from "./redux";

interface IProps {
  version?: Version;
  onZoomToDesign?: () => void;
}

const MapButtons: React.FC<IProps> = ({ version, onZoomToDesign }: IProps) => {
  const { map, drawControl, setDrawMode } = useContext(MapContext);
  const dispatch = useAppDispatch();
  const editMode = useSelector((state: Store) => state.project.editMode);
  const removeControl = useRef(false);

  const onClickMeasureHandler = () => {
    if (editMode === "measure") {
      dispatch(endMeasuring());
    } else {
      dispatch(beginMeasuring());
    }
  };

  const onClickZoomToDesignHandler = () => {
    if (onZoomToDesign) {
      onZoomToDesign();
      return;
    }
    dispatch(viewDesign(version?.BoundingBox, version?.SelectedArea));
  };

  const onClickZoomInHandler = () => {
    dispatch(zoomIn());
  };

  const onClickZoomOutHandler = () => {
    dispatch(zoomOut());
  };

  const handleDrawComplete = useCallback(
    (feature: Feature<LineString>) => {
      // If draw control was created here we need to remove it after use.
      // we delay to ensure the street view mode has finished processing
      delay(() => {
        if (removeControl.current && drawControl.current) {
          map?.removeControl(drawControl.current);
          drawControl.current = null;
          removeControl.current = false;
        }
      }, 0);

      openStreetViewUrl(feature);
    },
    [drawControl, map]
  );

  const onClickStreetViewHandler = useCallback(() => {
    // Indicate if we need to remove the control after use.
    // if there is no existing draw control we created one for street view & dispose after.
    removeControl.current = drawControl.current === null;
    setDrawMode("street_view", {
      onDraw: handleDrawComplete,
    });
    map?.fire("draw.modechange", { type: "draw.modechange", mode: "street_view" });
  }, [drawControl, setDrawMode, handleDrawComplete, map]);

  return (
    <BaseMapButtons
      editMode={editMode}
      onClickMeasure={onClickMeasureHandler}
      onClickZoomToDesign={onClickZoomToDesignHandler}
      onClickZoomIn={onClickZoomInHandler}
      onClickZoomOut={onClickZoomOutHandler}
      onClickStreetView={onClickStreetViewHandler}
      streetViewButton
    />
  );
};

export default MapButtons;
